import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';

import { FormItem, TextareaAutosize } from '@cw-ui/form-components';

import { FIELD_FORM_CONTROL_CLASS } from '../field-form-control-styles';

// MUI's OutlinedInput-based multiline (used by @cw-ui/form-components' Textarea)
// always renders @mui/material's TextareaAutosize, which recalculates and
// overwrites the textarea's height from its content on every render - so a
// user dragging the native resize handle gets snapped back on the next
// keystroke. @cw-ui/form-components' own TextareaAutosize renders a plain
// native textarea with no such height management, which lets users resize it
// freely.
//
// Two things need fixing on top of that native textarea, both via a CSS class
// on a wrapper (not a `style` prop on the textarea itself):
// - its own stylesheet sets width: 100% plus horizontal padding without
//   box-sizing: border-box, which (this app has no CssBaseline reset) makes it
//   render wider than sibling fields.
// - a `style` prop is reconciled by React on every re-render (e.g. on
//   validation/typing), which would wipe out the `height` the browser sets
//   directly on the DOM node while the user drags the native resize handle.
//   A stylesheet rule on a wrapper avoids touching the textarea's inline
//   style at all, so the user's manual resize persists across re-renders.
// No maxHeight is set - the user can expand it as tall as they want; only the
// horizontal dimension is constrained (resize: vertical) so it can't overflow
// the form's width.
const TEXTAREA_RESIZE_SX = {
  '& textarea': {
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '84px'
  }
};

const TextareaField = ({ field, value, onChange, onBlur, helperText, errorText, disabled = false }) => {
  const maxLength = field.validation?.maxLength;
  const count = maxLength ? `${(value ?? '').length}/${maxLength}` : undefined;
  const hasError = Boolean(errorText);

  return (
    <FormItem
      id={field.id}
      labelText={field.label}
      labelProps={{ required: field.required, error: hasError }}
      shrink
      size="medium"
      fullWidth
      className={FIELD_FORM_CONTROL_CLASS}
    >
      <Box sx={TEXTAREA_RESIZE_SX}>
        <TextareaAutosize
          id={field.id}
          placeholder={field.placeholder}
          value={value ?? ''}
          onChange={event => onChange(event.target.value)}
          onBlur={onBlur}
          count={count}
          error={hasError}
          helperText={errorText ?? helperText}
          disabled={disabled}
          rows={5}
          maxLength={maxLength}
        />
      </Box>
    </FormItem>
  );
};

TextareaField.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    validation: PropTypes.shape({ maxLength: PropTypes.number })
  }).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  helperText: PropTypes.string,
  errorText: PropTypes.string,
  disabled: PropTypes.bool
};

export default TextareaField;
